<template>
  <div class="admin-section">
    <div class="section-header">
      <h2 class="section-title">Patient Summaries</h2>
      <div style="display: flex; gap: 0.5rem;">
        <Button
          @click="loadSummaries"
          :disabled="isLoading"
          variant="default"
          size="sm"
          class="refresh-btn"
        >
          <span v-if="!isLoading">Refresh</span>
          <span v-else>Loading...</span>
        </Button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-row">
      <div class="filter-group">
        <Label for="filter-study">Filter by study</Label>
        <select id="filter-study" v-model="filters.study_id" class="filter-input" @change="loadSummaries">
          <option :value="''">All studies</option>
          <option v-for="study in studies" :key="study.id" :value="study.id">
            #{{ study.id }} — {{ study.name }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <Label for="filter-user">Filter by user id</Label>
        <Input
          id="filter-user"
          v-model="filters.user_id"
          placeholder="e.g. 42"
          @keyup.enter="loadSummaries"
        />
      </div>
      <div class="filter-actions">
        <Button @click="loadSummaries" variant="default" size="sm">Apply</Button>
        <Button
          @click="clearFilters"
          variant="default"
          size="sm"
          style="background-color: #64748b;"
        >
          Clear
        </Button>
      </div>
    </div>

    <div v-if="error" class="message error">{{ error }}</div>
    <div v-if="successMessage" class="message success">{{ successMessage }}</div>

    <div v-if="isLoading && summaries.length === 0" class="loading-state">
      Loading patient summaries...
    </div>
    <div v-else-if="summaries.length === 0" class="empty-state">
      No patient summaries found.
    </div>

    <div v-else class="summaries-list">
      <div
        v-for="summary in summaries"
        :key="summary.id"
        class="summary-item"
      >
        <div class="summary-header" @click="toggleSummary(summary.id)">
          <div class="summary-info">
            <div class="summary-title">
              Summary #{{ summary.id }} ·
              <span class="summary-study">{{ summary.study_name || `Study ${summary.study_id}` }}</span>
            </div>
            <div class="summary-meta">
              <span class="meta-pill">User #{{ summary.user_id }}</span>
              <span v-if="summary.user_email" class="meta-pill">{{ summary.user_email }}</span>
              <span class="meta-pill">{{ formatDate(summary.created_at) }}</span>
            </div>
          </div>
          <div class="summary-actions" @click.stop>
            <Button
              @click="startEdit(summary)"
              :disabled="isSaving[summary.id]"
              variant="default"
              size="sm"
              style="background-color: #3b82f6; margin-right: 0.5rem;"
            >
              Edit
            </Button>
            <Button
              @click="openNotify(summary)"
              :disabled="isNotifying[summary.id]"
              variant="default"
              size="sm"
              style="background-color: #0ea5e9; margin-right: 0.5rem;"
            >
              Notify
            </Button>
            <Button
              @click="deleteSummary(summary)"
              :disabled="isDeleting[summary.id]"
              variant="destructive"
              size="sm"
            >
              <span v-if="!isDeleting[summary.id]">Delete</span>
              <span v-else>Deleting...</span>
            </Button>
            <span class="expand-icon" :class="{ expanded: expanded[summary.id] }">▼</span>
          </div>
        </div>

        <div v-if="expanded[summary.id]" class="summary-details">
          <!-- Edit form -->
          <div v-if="editing[summary.id]" class="edit-form">
            <h3 class="form-title">Edit Patient Summary #{{ summary.id }}</h3>
            <div class="form-group">
              <Label :for="`edit-study-${summary.id}`">Reassign to Study</Label>
              <select
                :id="`edit-study-${summary.id}`"
                v-model="editForm[summary.id].study_id"
                class="filter-input"
              >
                <option v-for="study in studies" :key="study.id" :value="study.id">
                  #{{ study.id }} — {{ study.name }}
                </option>
              </select>
              <p
                v-if="editForm[summary.id].study_id !== summary.study_id"
                class="warn-text"
              >
                Reassigning to a different study will revoke any existing
                researcher notification links for this summary.
              </p>
            </div>
            <div class="form-group">
              <Label :for="`edit-text-${summary.id}`">Summary text</Label>
              <textarea
                :id="`edit-text-${summary.id}`"
                v-model="editForm[summary.id].summary_text"
                class="form-textarea"
                rows="8"
              ></textarea>
            </div>
            <div class="form-actions">
              <Button
                @click="saveEdit(summary)"
                :disabled="isSaving[summary.id]"
                variant="default"
                size="sm"
              >
                <span v-if="!isSaving[summary.id]">Save</span>
                <span v-else>Saving...</span>
              </Button>
              <Button
                @click="cancelEdit(summary.id)"
                variant="default"
                size="sm"
                style="background-color: #64748b;"
              >
                Cancel
              </Button>
            </div>
          </div>

          <!-- Notify panel -->
          <div v-if="notifying[summary.id]" class="edit-form">
            <h3 class="form-title">Notify Researchers</h3>
            <p class="muted-text">
              Researchers assigned to <strong>{{ summary.study_name || `Study ${summary.study_id}` }}</strong>.
              Select who to notify, or leave all selected to notify everyone.
            </p>
            <div v-if="loadingResearchers[summary.id]" class="loading-state">
              Loading researchers...
            </div>
            <div
              v-else-if="!studyResearchers[summary.study_id] || studyResearchers[summary.study_id].length === 0"
              class="empty-state"
            >
              No researchers are assigned to this study. Assign researchers
              from the Researchers tab first.
            </div>
            <div v-else class="researcher-checklist">
              <label
                v-for="r in studyResearchers[summary.study_id]"
                :key="r.id"
                class="researcher-row"
              >
                <input
                  type="checkbox"
                  :value="r.id"
                  v-model="notifyForm[summary.id].researcher_ids"
                />
                <span class="researcher-name">{{ r.name }}</span>
                <span class="researcher-email">{{ r.email }}</span>
              </label>
            </div>
            <div class="form-group">
              <Label :for="`notify-note-${summary.id}`">Optional note (audit only)</Label>
              <textarea
                :id="`notify-note-${summary.id}`"
                v-model="notifyForm[summary.id].note"
                class="form-textarea"
                rows="3"
                placeholder="e.g. Re-sending after study reassignment"
              ></textarea>
            </div>
            <div class="form-actions">
              <Button
                @click="sendNotifications(summary)"
                :disabled="isNotifying[summary.id] || !canNotify(summary)"
                variant="default"
                size="sm"
              >
                <span v-if="!isNotifying[summary.id]">Send notifications</span>
                <span v-else>Sending...</span>
              </Button>
              <Button
                @click="cancelNotify(summary.id)"
                variant="default"
                size="sm"
                style="background-color: #64748b;"
              >
                Cancel
              </Button>
            </div>

            <div v-if="notifyResults[summary.id]" class="notify-results">
              <h4>Results</h4>
              <ul>
                <li
                  v-for="r in notifyResults[summary.id].results"
                  :key="r.researcher_id"
                  :class="['notify-result', `status-${r.status}`]"
                >
                  Researcher #{{ r.researcher_id }} —
                  <strong>{{ r.status }}</strong>
                  <span v-if="r.reason"> ({{ r.reason }})</span>
                  <span v-if="r.email_sent"> · email sent</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Read-only view -->
          <div class="detail-section">
            <strong>Summary text</strong>
            <pre class="summary-text">{{ summary.summary_text }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { API_ENDPOINTS, HTTP_STATUS, ROUTES, ERROR_MESSAGES } from '@/constants'

export default {
  name: 'PatientSummariesManagement',
  components: { Button, Input, Label },
  props: {
    isAuthenticated: { type: Boolean, required: true },
    isAdmin: { type: Boolean, required: true }
  },
  data() {
    return {
      summaries: [],
      studies: [],
      isLoading: false,
      error: '',
      successMessage: '',
      filters: { study_id: '', user_id: '' },
      expanded: {},
      editing: {},
      editForm: {},
      isSaving: {},
      isDeleting: {},
      notifying: {},
      notifyForm: {},
      isNotifying: {},
      notifyResults: {},
      studyResearchers: {},
      loadingResearchers: {}
    }
  },
  mounted() {
    this.loadStudies()
    this.loadSummaries()
  },
  methods: {
    handleAuthError(error) {
      if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
        this.$store.dispatch('auth/logout')
        this.$router.push(ROUTES.HOME)
        return true
      }
      return false
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async loadStudies() {
      try {
        const response = await api.get(API_ENDPOINTS.ADMIN_STUDIES)
        this.studies = response.data || []
      } catch (error) {
        if (this.handleAuthError(error)) return
        // Non-fatal: study list is only used for filter/reassignment dropdowns
        console.error('Failed to load studies for filter/reassignment', error)
      }
    },
    async loadSummaries() {
      if (!this.isAuthenticated || !this.isAdmin) return
      this.isLoading = true
      this.error = ''
      this.successMessage = ''
      try {
        const params = {}
        if (this.filters.study_id !== '' && this.filters.study_id !== null) {
          params.study_id = this.filters.study_id
        }
        if (this.filters.user_id !== '' && this.filters.user_id !== null) {
          const uid = parseInt(this.filters.user_id, 10)
          if (!Number.isNaN(uid)) params.user_id = uid
        }
        const response = await api.get(API_ENDPOINTS.ADMIN_PATIENT_SUMMARIES, { params })
        this.summaries = response.data || []
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.error = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      } finally {
        this.isLoading = false
      }
    },
    clearFilters() {
      this.filters = { study_id: '', user_id: '' }
      this.loadSummaries()
    },
    toggleSummary(id) {
      this.expanded[id] = !this.expanded[id]
    },
    startEdit(summary) {
      this.expanded[summary.id] = true
      this.editing[summary.id] = true
      this.notifying[summary.id] = false
      this.editForm[summary.id] = {
        study_id: summary.study_id,
        summary_text: summary.summary_text
      }
    },
    cancelEdit(id) {
      this.editing[id] = false
    },
    async saveEdit(summary) {
      if (!this.isAuthenticated || !this.isAdmin) return
      const form = this.editForm[summary.id]
      const payload = {}
      if (form.study_id !== summary.study_id) payload.study_id = form.study_id
      if (form.summary_text !== summary.summary_text) payload.summary_text = form.summary_text

      if (Object.keys(payload).length === 0) {
        this.editing[summary.id] = false
        return
      }

      const studyChanged = 'study_id' in payload
      if (studyChanged) {
        const ok = confirm(
          'Reassigning this summary to a different study will revoke any '
          + 'existing researcher notification links for it. Continue?'
        )
        if (!ok) return
      }

      this.isSaving[summary.id] = true
      this.error = ''
      try {
        const response = await api.put(
          API_ENDPOINTS.ADMIN_PATIENT_SUMMARY(summary.id),
          payload
        )
        const idx = this.summaries.findIndex(s => s.id === summary.id)
        if (idx !== -1) this.summaries[idx] = response.data
        this.editing[summary.id] = false
        this.successMessage = `Summary #${summary.id} updated.`
        // Drop cached researcher list for the prior study so the notify panel
        // reloads if the admin opens it next.
        if (studyChanged) {
          delete this.studyResearchers[response.data.study_id]
        }
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.error = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      } finally {
        this.isSaving[summary.id] = false
      }
    },
    async deleteSummary(summary) {
      if (!this.isAuthenticated || !this.isAdmin) return
      const ok = confirm(
        `Delete patient summary #${summary.id}? This will also remove any `
        + 'related researcher notifications. This action cannot be undone.'
      )
      if (!ok) return
      this.isDeleting[summary.id] = true
      this.error = ''
      try {
        await api.delete(API_ENDPOINTS.ADMIN_PATIENT_SUMMARY(summary.id))
        this.summaries = this.summaries.filter(s => s.id !== summary.id)
        this.successMessage = `Summary #${summary.id} deleted.`
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.error = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      } finally {
        this.isDeleting[summary.id] = false
      }
    },
    async openNotify(summary) {
      this.expanded[summary.id] = true
      this.notifying[summary.id] = true
      this.editing[summary.id] = false
      this.notifyResults[summary.id] = null
      if (!this.notifyForm[summary.id]) {
        this.notifyForm[summary.id] = { researcher_ids: [], note: '' }
      }
      await this.loadStudyResearchers(summary.study_id)
      // Default-select all assigned researchers
      const list = this.studyResearchers[summary.study_id] || []
      this.notifyForm[summary.id].researcher_ids = list.map(r => r.id)
    },
    cancelNotify(id) {
      this.notifying[id] = false
    },
    canNotify(summary) {
      const list = this.studyResearchers[summary.study_id] || []
      if (list.length === 0) return false
      const form = this.notifyForm[summary.id]
      return !!form && Array.isArray(form.researcher_ids) && form.researcher_ids.length > 0
    },
    async loadStudyResearchers(studyId) {
      if (this.studyResearchers[studyId]) return
      this.loadingResearchers[studyId] = true
      try {
        // Fetch all researchers, then filter to those assigned to this study.
        // Reuses existing endpoints rather than introducing a new one.
        const response = await api.get(API_ENDPOINTS.ADMIN_RESEARCHERS)
        const all = response.data || []
        this.studyResearchers[studyId] = all.filter(r =>
          Array.isArray(r.studies) && r.studies.some(s => s.id === studyId)
        )
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.studyResearchers[studyId] = []
        console.error(`Failed to load researchers for study ${studyId}`, error)
      } finally {
        this.loadingResearchers[studyId] = false
      }
    },
    async sendNotifications(summary) {
      if (!this.isAuthenticated || !this.isAdmin) return
      const form = this.notifyForm[summary.id]
      this.isNotifying[summary.id] = true
      this.error = ''
      try {
        const payload = {
          researcher_ids: form.researcher_ids,
          note: form.note ? form.note : null
        }
        const response = await api.post(
          API_ENDPOINTS.ADMIN_NOTIFY_PATIENT_SUMMARY(summary.id),
          payload
        )
        this.notifyResults[summary.id] = response.data
        this.successMessage =
          `Notifications dispatched: ${response.data.notified_count} new, `
          + `${response.data.results.length - response.data.notified_count} skipped/failed.`
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.error = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      } finally {
        this.isNotifying[summary.id] = false
      }
    }
  }
}
</script>

<style scoped>
.admin-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.refresh-btn {
  background-color: #3b82f6;
  color: #ffffff;
}

.message {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
}

.message.error {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #ef4444;
}

.message.success {
  background-color: #dcfce7;
  color: #166534;
  border: 1px solid #22c55e;
}

.loading-state,
.empty-state {
  padding: 1.5rem;
  text-align: center;
  color: #64748b;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 220px;
  flex: 1;
}

.filter-input {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: #ffffff;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
}

.summaries-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.summary-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background-color: #ffffff;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1rem;
  cursor: pointer;
}

.summary-info {
  flex: 1;
  min-width: 0;
}

.summary-title {
  font-weight: 600;
  color: #1e293b;
}

.summary-study {
  color: #475569;
  font-weight: 500;
}

.summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.meta-pill {
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background-color: #f1f5f9;
  color: #475569;
}

.summary-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.expand-icon {
  display: inline-block;
  margin-left: 0.5rem;
  color: #64748b;
  transition: transform 0.2s;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.summary-details {
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.detail-section {
  margin-bottom: 1rem;
}

.summary-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8125rem;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-top: 0.25rem;
  max-height: 24rem;
  overflow: auto;
}

.edit-form {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.form-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 0.5rem;
}

.warn-text {
  margin: 0;
  font-size: 0.8125rem;
  color: #b45309;
}

.muted-text {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.researcher-checklist {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 14rem;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.5rem;
  background-color: #f8fafc;
}

.researcher-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.375rem;
  cursor: pointer;
}

.researcher-row:hover {
  background-color: #eef2f7;
  border-radius: 0.25rem;
}

.researcher-name {
  font-weight: 500;
  color: #1e293b;
}

.researcher-email {
  color: #64748b;
  font-size: 0.8125rem;
}

.notify-results {
  margin-top: 0.5rem;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.75rem;
}

.notify-results ul {
  list-style: none;
  padding: 0;
  margin: 0.25rem 0 0 0;
}

.notify-result {
  font-size: 0.875rem;
  padding: 0.125rem 0;
}

.status-sent {
  color: #166534;
}

.status-already_sent {
  color: #475569;
}

.status-failed {
  color: #991b1b;
}
</style>
